import { confirmSignUp, resendSignUpCode, signIn, signUp } from 'aws-amplify/auth';
import { useState } from 'react';

export type SignUpStep = 'signup' | 'confirm' | 'complete';

interface UseSignUpOptions {
  autoSignIn: boolean;
  onDone: () => void;
}

export function useSignUp({ autoSignIn, onDone }: UseSignUpOptions) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<SignUpStep>('signup');

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('パスワードが一致しません');
      return;
    }

    setLoading(true);

    try {
      const result = await signUp({
        username: email,
        password,
        options: {
          userAttributes: { email },
          autoSignIn,
        },
      });

      if (result.nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
        setStep('confirm');
      } else if (result.nextStep.signUpStep === 'DONE') {
        onDone();
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'UsernameExistsException') {
        try {
          // signIn で確認状態を判定（UserNotConfirmedException は preventUserExistenceErrors でもマスクされない）
          await signIn({ username: email, password });
          // サインイン成功 = 確認済みユーザー（パスワード一致）
          onDone();
          return;
        } catch (signInErr) {
          if (signInErr instanceof Error && signInErr.name === 'UserNotConfirmedException') {
            // 未確認ユーザー → 確認コードを再送信
            await resendSignUpCode({ username: email });
            setStep('confirm');
            return;
          }
          // 確認済みユーザー（パスワード不一致等）
          setError('このメールアドレスは既に登録されています');
          return;
        }
      }
      const message = err instanceof Error ? err.message : 'サインアップに失敗しました';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await confirmSignUp({
        username: email,
        confirmationCode,
      });

      if (result.isSignUpComplete) {
        setStep('complete');
      } else {
        setError(`追加の確認が必要です: ${result.nextStep.signUpStep}`);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : '確認に失敗しました';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    confirmationCode,
    setConfirmationCode,
    error,
    loading,
    step,
    handleSignUp,
    handleConfirm,
  };
}
